/**
 * filingContextFormat — centralized formatters for values carried on
 * `FilingContext`.
 *
 * These replace inline `date-fns` / `luxon` formatting and scattered
 * `"$" + (cents / 100)` arithmetic at call sites. Per the architecture doc
 * §2.6, **the backend asserts and the frontend formats** — these helpers
 * own the latter for regulatory-facing surfaces.
 *
 * Devil's advocate:
 *   - `formatDeadline` takes ISO-8601 (`YYYY-MM-DD`) because that's what the
 *     backend emits. We parse as UTC calendar date (no implicit TZ shift)
 *     and format in the user's locale. A user in PT should still see
 *     "June 30, 2026" for a NYC deadline, not "June 29" due to TZ wobble.
 *   - `formatFeeCents` uses `Intl.NumberFormat` so localization is free when
 *     we add it. Zero-cent amounts are formatted as "$0" deliberately — the
 *     caller decides whether to hide the whole surface when there's no fee.
 */

import { DateTime } from "luxon";

import type { FilingContextLaw } from "@/api/filingContext";

/**
 * Format a backend deadline (ISO-8601, `YYYY-MM-DD` or full ISO) as a
 * long-form calendar date, e.g. "June 30, 2026".
 *
 * Returns `null` on empty / malformed input so callers can branch cleanly.
 */
export function formatDeadline(iso: string | null | undefined): string | null {
  if (!iso) return null;
  // Parse as a local calendar date; the backend emits YYYY-MM-DD and we
  // want to render the date it names, not a moment-shifted one.
  const dt = DateTime.fromISO(iso, { zone: "utc" });
  if (!dt.isValid) return null;
  return dt.toLocaleString({
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format a cents-denominated fee as a USD dollar string, e.g. "$60" or
 * "$100.50". Sub-dollar precision is preserved only when non-zero.
 *
 * Returns `null` for nullish input so callers can decide whether to render
 * "No fee" vs. hide the field entirely.
 */
export function formatFeeCents(
  cents: number | null | undefined,
): string | null {
  if (cents === null || cents === undefined) return null;
  if (!Number.isFinite(cents)) return null;
  const dollars = cents / 100;
  const hasCents = Math.round(dollars * 100) % 100 !== 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  }).format(dollars);
}

/**
 * Format the law display name. Prefers `law.displayName` (owner-safe,
 * "Local Law 97") over `law.shortName` ("LL97") over the raw `law.code`.
 *
 * Returns an empty string only if the caller passed `undefined` — the
 * contract requires `law.code` so a non-undefined `FilingContextLaw` always
 * produces text.
 */
export function formatLaw(law: FilingContextLaw | undefined): string {
  if (!law) return "";
  return law.displayName ?? law.shortName ?? law.code;
}
